import { useEffect, useMemo } from "react";
import { useNode } from "@craftjs/core";
import { useFormikContext } from "formik";
import TextInputSettings from "../settings/TextInputSettings";
import useResponsiveValues from "@/components/editor/hooks/useResponsiveValues";
import { CssClass } from "@/components/editor/settings/ClassSettings";
import { TextField, InputAdornment } from "@mui/material";
import slugify from "slugify";
import DynamicHugeIcon from "@/components/HugeIcon/DynamicHugeIcon";

interface TextInputProps extends CssClass {
  label: string;
  value: string;
  variant?: "outlined" | "filled" | "standard";
  fullWidth?: boolean;
  size?: "small" | "medium";
  rows?: number;
  helperText?: string;
  required?: boolean;
  icon?: string;
  iconLocation?: "start" | "end";
  iconStroke?: number;
  iconSize?: number;
  iconColor?: string;
}

const TextInputBlock = ({ label, value, variant, fullWidth, size, rows, helperText, required, icon, iconLocation, iconStroke, iconSize, iconColor }: Partial<TextInputProps>) => {
  const responsive = useResponsiveValues();
  const formik = useFormikContext<any>();
  const fieldId = useMemo(() => slugify(label ?? "", { lower: true }), [label]);

  useEffect(() => {
    if (formik && value) {
      formik.setFieldValue(fieldId, value);
    }
  }, [fieldId]);

  const isFormik = !!formik;
  const fieldValue = isFormik ? formik.values[fieldId] : value;

  const handleChange = (e: any) => {
    if (isFormik) {
      formik.handleChange(e);
    }
  };

  const {
    id,
    connectors: { connect, drag },
  } = useNode((state) => {
    return {
      hasSelectedNode: state.events.selected,
      hasDraggedNode: state.events.dragged,
    };
  });

  const resolvedColor = useMemo(() => responsive.getValue(iconColor), [iconColor, responsive]);

  const adornment = icon ? (
    <InputAdornment position={iconLocation ?? "start"}>
      <DynamicHugeIcon name={icon} size={iconSize ?? 20} strokeWidth={iconStroke ?? 2} color={resolvedColor} />
    </InputAdornment>
  ) : null;

  return (
    <TextField
      key={`${id}-${responsive.viewport}`}
      ref={(ref: any) => connect(drag(ref))}
      name={fieldId}
      label={label}
      value={fieldValue}
      onChange={handleChange}
      variant={variant}
      fullWidth={fullWidth}
      size={size}
      minRows={rows}
      multiline={!!(rows && rows > 1)}
      helperText={helperText}
      required={required}
      InputProps={{
        startAdornment: iconLocation === "start" ? adornment : undefined,
        endAdornment: iconLocation === "end" ? adornment : undefined,
      }}
    />
  );
};

TextInputBlock.craft = {
  displayName: "Campo de texto",
  props: {
    label: "Etiqueta",
    value: "",
    variant: "outlined",
    fullWidth: true,
    size: "medium",
    rows: 1,
    type: "text",
    helperText: "",
    required: false,
    icon: "",
    iconLocation: "start",
    iconStroke: 2,
    iconSize: 20,
    iconColor: "#000000",
  },
  related: {
    settings: TextInputSettings,
  },
  custom: {
    formField: true,
  },
};

export default TextInputBlock;
